import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import StatusPill from '../components/StatusPill';

// USD amounts render with 4 decimals -- at the per-call scale this table
// tracks (a single agent turn rarely exceeds a few cents), 2 decimals
// would round most individual rows to $0.00 and hide real variation.
function formatUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.0000';
  return `$${n.toFixed(4)}`;
}

function formatTokens(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('es-MX');
}

export function webhookDeliveryState(event = {}) {
  const note = event.processing_note || '';
  if (note.includes('came from a Forge delivery configuration branch')) return {
    title: 'Ejecución de configuración de entrega',
    detail: 'Esta ejecución valida la configuración de Delivery; no corresponde al código entregable y no libera ninguna Spec.',
    color: 'steel',
  };
  if (note.includes('not associated with a Forge delivery spec')) return {
    title: 'Ejecución sin asociación a Forge Spec',
    detail: 'GitHub Actions no pudo asociar esta ejecución a una Spec; no liberó ninguna entrega.',
    color: 'amber',
  };
  if (note.includes('is not a Forge delivery workflow')) return {
    title: 'Workflow fuera del contrato de entrega',
    detail: 'La ejecución fue registrada para auditoría, pero no corresponde a CI o despliegue de FORGE.',
    color: 'steel',
  };
  if (note.includes('recorded ci evidence')) return {
    title: 'Evidencia de CI registrada',
    detail: 'FORGE actualizó el gate de CI desde GitHub Actions.',
    color: note.includes("'passed'") ? 'green' : 'red',
  };
  if (note.includes('recorded deployment evidence')) return {
    title: 'Evidencia de despliegue registrada',
    detail: 'FORGE actualizó el gate de despliegue desde GitHub Actions.',
    color: note.includes("'passed'") ? 'green' : 'amber',
  };
  return {
    title: event.event_type === 'workflow_run' ? 'Ejecución de GitHub Actions' : `${event.event_type}${event.action ? ` · ${event.action}` : ''}`,
    detail: note || 'Evento recibido por FORGE.',
    color: event.processed ? 'green' : 'amber',
  };
}

function Observe() {
  const { apiUrl, overview, userId, currentUser } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [llmUsage, setLlmUsage] = useState(null);
  const [llmUsageError, setLlmUsageError] = useState(null);
  const [adminAuditLog, setAdminAuditLog] = useState(null);
  const [adminAuditError, setAdminAuditError] = useState(null);
  const [prReviews, setPrReviews] = useState(null);
  const [prReviewsError, setPrReviewsError] = useState(null);
  const isAdmin = Boolean(currentUser?.can_admin);

  useEffect(() => {
    if (!userId) return;
    fetch(`${apiUrl}/api/webhooks/events?user_id=${userId}&limit=20`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudieron cargar los eventos.');
        setEvents(data);
      })
      .catch((err) => setError(err.message === 'Failed to fetch' ? 'Backend no disponible para eventos.' : err.message));

    fetch(`${apiUrl}/api/audit-log?user_id=${userId}&limit=15`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el audit log.');
        setAuditLog(data);
      })
      .catch((err) => setAuditError(err.message === 'Failed to fetch' ? 'Backend no disponible.' : err.message));

    fetch(`${apiUrl}/api/audit-log/llm-usage?user_id=${userId}&limit=15`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el uso de tokens.');
        setLlmUsage(data);
      })
      .catch((err) => setLlmUsageError(err.message === 'Failed to fetch' ? 'Backend no disponible.' : err.message));

    fetch(`${apiUrl}/api/audit-log/pr-reviews?user_id=${userId}&limit=15`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el compliance de PRs.');
        setPrReviews(data);
      })
      .catch((err) => setPrReviewsError(err.message === 'Failed to fetch' ? 'Backend no disponible.' : err.message));
  }, [apiUrl, userId]);

  useEffect(() => {
    if (!userId || !isAdmin) return;
    fetch(`${apiUrl}/api/admin/audit-log?user_id=${userId}&limit=15`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'No se pudo cargar la auditoría administrativa.');
        setAdminAuditLog(data);
      })
      .catch((err) => setAdminAuditError(err.message === 'Failed to fetch' ? 'Backend no disponible.' : err.message));
  }, [apiUrl, userId, isAdmin]);

  return (
    <div className="forge-page">
      <div className="forge-page-header">
        <div>
          <div className="forge-section-eyebrow">Módulo 03 · Observabilidad</div>
          <h1 className="forge-title">Observar</h1>
          <p className="forge-lead">
            Actividad GitHub, tareas de agentes, tokens/costo de LLM y señales de semáforo
            usando datos actuales.
          </p>
        </div>
      </div>

      <div className="forge-grid three">
        <div className="forge-panel"><span className="forge-kbd">repos</span><h2>{overview?.repos?.total ?? 0}</h2><p>Repositorios conectados a Forge.</p></div>
        <div className="forge-panel"><span className="forge-kbd">tasks</span><h2>{Object.values(overview?.tasks_by_status || {}).reduce((a, b) => a + b, 0)}</h2><p>Tareas de agente registradas.</p></div>
        <div className="forge-panel"><span className="forge-kbd">tool_calls</span><h2>{auditLog?.summary?.total_calls ?? 0}</h2><p>Invocaciones de herramientas por agentes.</p></div>
      </div>

      {auditLog?.summary && (
        <div className="forge-grid three" style={{ marginTop: '0.5rem' }}>
          <div className="forge-panel"><span className="forge-kbd">success</span><h2>{auditLog.summary.successful}</h2><p>Llamadas exitosas.</p></div>
          <div className="forge-panel"><span className="forge-kbd">failed</span><h2>{auditLog.summary.failed}</h2><p>Llamadas fallidas.</p></div>
          <div className="forge-panel"><span className="forge-kbd">agents</span><h2>{Object.keys(auditLog.summary.by_agent).length}</h2><p>Roles de agente activos.</p></div>
        </div>
      )}

      {llmUsage && (
        <div className="forge-grid three" style={{ marginTop: '0.5rem' }}>
          <div className="forge-panel"><span className="forge-kbd">llm_calls</span><h2>{llmUsage.total_calls}</h2><p>Llamadas a modelos registradas.</p></div>
          <div className="forge-panel"><span className="forge-kbd">tokens</span><h2>{formatTokens(llmUsage.total_tokens)}</h2><p>{formatTokens(llmUsage.total_input_tokens)} entrada · {formatTokens(llmUsage.total_output_tokens)} salida.</p></div>
          <div className="forge-panel"><span className="forge-kbd">est_cost</span><h2>{formatUsd(llmUsage.total_estimated_cost_usd)}</h2><p>Estimado con precios publicados de Bedrock.</p></div>
        </div>
      )}

      {llmUsage?.daily_cost_alert && (
        <div className="message error" style={{ marginTop: '0.5rem' }}>
          ⚠️ Costo de LLM de hoy ({formatUsd(llmUsage.today_cost_usd)}) supera el
          umbral configurado ({formatUsd(llmUsage.daily_cost_alert_threshold_usd)}).
        </div>
      )}

      <h2 className="forge-section-title"><span className="num">A.</span> Actividad reciente</h2>
      <div className="forge-list">
        {(overview?.activity || []).slice(0, 8).map((item, index) => (
          <div key={`${item.kind}-${index}`} className="forge-row">
            <div className="forge-row-main">
              <div className="forge-row-title">{item.label}</div>
              <div className="forge-row-detail">{item.repo_full_name || 'Forge'} · {item.detail || item.kind}</div>
            </div>
            <StatusPill color={item.status === 'red' || item.status === 'failed' ? 'red' : item.status === 'green' || item.status === 'approved' ? 'green' : 'amber'}>
              {item.status || item.kind}
            </StatusPill>
          </div>
        ))}
        {(!overview?.activity || overview.activity.length === 0) && <div className="forge-empty">Sin actividad todavía.</div>}
      </div>

      <h2 className="forge-section-title"><span className="num">B.</span> Agent Tool Audit Log</h2>
      <div className="forge-panel">
        {auditError && <div className="message error">{auditError}</div>}
        {!auditError && (!auditLog || auditLog.entries.length === 0) && (
          <div className="forge-empty">No hay invocaciones de herramientas registradas aún.</div>
        )}
        {auditLog?.summary && Object.keys(auditLog.summary.by_tool).length > 0 && (
          <div className="forge-grid two" style={{ marginBottom: '1rem' }}>
            <div>
              <strong style={{ fontSize: '0.8rem', color: 'var(--forge-muted)' }}>Por agente:</strong>
              {Object.entries(auditLog.summary.by_agent).map(([role, count]) => (
                <div key={role} className="forge-row-detail">{role}: {count}</div>
              ))}
            </div>
            <div>
              <strong style={{ fontSize: '0.8rem', color: 'var(--forge-muted)' }}>Por herramienta:</strong>
              {Object.entries(auditLog.summary.by_tool).map(([tool, count]) => (
                <div key={tool} className="forge-row-detail">{tool}: {count}</div>
              ))}
            </div>
          </div>
        )}
        <div className="forge-list">
          {(auditLog?.entries || []).map((entry) => (
            <div key={entry.id} className="forge-row">
              <div className="forge-row-main">
                <div className="forge-row-title">{entry.agent_role} → {entry.tool_name}</div>
                <div className="forge-row-detail">
                  {entry.repo_full_name} · {new Date(entry.created_at).toLocaleString()}
                </div>
              </div>
              <StatusPill color={entry.success ? 'green' : 'red'}>
                {entry.success ? 'ok' : 'fail'}
              </StatusPill>
            </div>
          ))}
        </div>
      </div>

      <h2 className="forge-section-title"><span className="num">C.</span> Tokens y costo por agente</h2>
      <div className="forge-panel">
        {llmUsageError && <div className="message error">{llmUsageError}</div>}
        {!llmUsageError && (!llmUsage || llmUsage.total_calls === 0) && (
          <div className="forge-empty">No hay llamadas a modelos registradas aún.</div>
        )}
        {llmUsage && Object.keys(llmUsage.by_agent_role).length > 0 && (
          <div className="forge-list" style={{ marginBottom: '1rem' }}>
            {Object.entries(llmUsage.by_agent_role).map(([role, stats]) => (
              <div key={role} className="forge-row">
                <div className="forge-row-main">
                  <div className="forge-row-title">{role}</div>
                  <div className="forge-row-detail">
                    {stats.calls} llamada{stats.calls === 1 ? '' : 's'} ·{' '}
                    {formatTokens(stats.total_tokens)} tokens ({formatTokens(stats.input_tokens)} in / {formatTokens(stats.output_tokens)} out)
                  </div>
                </div>
                <StatusPill color="steel">{formatUsd(stats.estimated_cost_usd)}</StatusPill>
              </div>
            ))}
          </div>
        )}
        <div className="forge-list">
          {(llmUsage?.recent || []).map((inv) => (
            <div key={inv.id} className="forge-row">
              <div className="forge-row-main">
                <div className="forge-row-title">{inv.agent_role}</div>
                <div className="forge-row-detail">
                  {inv.repo_full_name || 'Forge'} · {formatTokens(inv.total_tokens)} tokens
                  {inv.latency_ms ? ` · ${(inv.latency_ms / 1000).toFixed(1)}s` : ''} ·{' '}
                  {new Date(inv.created_at).toLocaleString()}
                </div>
              </div>
              <StatusPill color="blue">{formatUsd(inv.estimated_cost_usd)}</StatusPill>
            </div>
          ))}
        </div>
      </div>

      <h2 className="forge-section-title"><span className="num">D.</span> Webhooks GitHub</h2>
      <div className="forge-panel">
        {error && <div className="message error">{error}</div>}
        {!error && events.length === 0 && <div className="forge-empty">No hay eventos webhook para los repos conectados.</div>}
        <div className="forge-list">
          {events.map((event) => {
            const state = webhookDeliveryState(event);
            return <div key={event.id} className="forge-row">
              <div className="forge-row-main">
                <div className="forge-row-title">{state.title}</div>
                <div className="forge-row-detail">{event.repo_full_name} · {state.detail}</div>
              </div>
              <StatusPill color={state.color}>{event.processed ? 'Procesado' : 'Pendiente'}</StatusPill>
            </div>;
          })}
        </div>
      </div>
      <h2 className="forge-section-title"><span className="num">E.</span> Compliance de PRs (PR Review Agent)</h2>
      <div className="forge-panel">
        {prReviewsError && <div className="message error">{prReviewsError}</div>}
        {!prReviewsError && (!prReviews || prReviews.length === 0) && (
          <div className="forge-empty">Sin reviews de PRs registradas aún.</div>
        )}
        <div className="forge-list">
          {(prReviews || []).map((review) => (
            <div key={review.id} className="forge-row">
              <div className="forge-row-main">
                <div className="forge-row-title">
                  {review.repo_full_name} · PR #{review.pr_number} · {review.spec_id}
                </div>
                <div className="forge-row-detail">
                  Compliance: {review.compliance_level} · {review.pr_state} ·{' '}
                  {new Date(review.created_at).toLocaleString()}
                </div>
              </div>
              <StatusPill color={review.semaphore === 'red' ? 'red' : review.semaphore === 'green' ? 'green' : 'amber'}>
                {review.semaphore}
              </StatusPill>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <>
          <h2 className="forge-section-title"><span className="num">F.</span> Auditoría de acciones administrativas</h2>
          <div className="forge-panel">
            {adminAuditError && <div className="message error">{adminAuditError}</div>}
            {!adminAuditError && (!adminAuditLog || adminAuditLog.length === 0) && (
              <div className="forge-empty">Sin acciones administrativas registradas aún.</div>
            )}
            <div className="forge-list">
              {(adminAuditLog || []).map((entry) => (
                <div key={entry.id} className="forge-row">
                  <div className="forge-row-main">
                    <div className="forge-row-title">
                      @{entry.actor_username} · {entry.action}
                      {entry.target_username && ` → @${entry.target_username}`}
                    </div>
                    <div className="forge-row-detail">
                      {entry.action === 'roles_changed' && entry.details
                        ? `${(entry.details.roles_removed || []).join(', ') || '—'} → ${(entry.details.roles_added || []).join(', ') || '—'} · `
                        : ''}
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Observe;
